import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  SparklesIcon,
  PencilIcon,
  CurrencyEuroIcon,
  CheckIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  EyeSlashIcon,
  EyeIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import type { Payment } from '../../types';
import type { AffairePaymentStats } from '../PaymentTable/PaymentTable';

interface PaymentRowMultiProps {
  payment: Payment;
  onUpdateAffaire: (paymentId: number, affaireId: string) => void;
  onValidate: (paymentId: number, validate: boolean) => void;
  onSmartAllocate: (payment: Payment) => void;
  onIgnore?: (paymentId: number, isIgnored: boolean) => void;
  onAcceptPayment?: (payment: Payment, affaire?: any) => void;
  onUnacceptPayment?: (paymentId: number, affaireId: string) => void;
  affairePaymentStats?: Record<string, AffairePaymentStats>;
  onFilterByAffaire?: (affaireId: string) => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

export const PaymentRowMulti: React.FC<PaymentRowMultiProps> = ({
  payment,
  onSmartAllocate,
  onIgnore,
  onAcceptPayment,
  onUnacceptPayment,
  affairePaymentStats,
  onFilterByAffaire,
  isExpanded = false,
  onToggleExpanded
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.includes('/bureau') ? '/bureau' : '/admin';
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);

  // Use props for expanded state, with fallback to toggle handler
  const expandedAffaires = isExpanded;
  const setExpandedAffaires = (value: boolean | ((prev: boolean) => boolean)) => {
    if (onToggleExpanded) {
      // If we have a toggle handler, use it (this will close other dropdowns)
      const newValue = typeof value === 'function' ? value(expandedAffaires) : value;
      if (newValue !== expandedAffaires) {
        onToggleExpanded();
      }
    }
  };

  // Check if payment has multiple affaires
  const hasMultipleAffaires = payment.affaires && payment.affaires.length > 1;
  const hasAnyAffaire = (payment.affaires && payment.affaires.length > 0) || payment.affaire_id;

  // Get primary affaire (for backward compatibility)
  const primaryAffaire = payment.affaires?.[0] || (payment.affaire_id ? {
    affaire_id: payment.affaire_id,
    amount_allocated: payment.amount,
    expected_amount: payment.total_expected || payment.amount,
    is_validated: payment.is_validated || false,
    payment_status: payment.payment_status,
    match_type: payment.total_expected && Math.abs(payment.amount - payment.total_expected) < 0.01 ? 'EXACT' : 'MANUAL'
  } : null);

  // Check validation status - only consider truly paid (PAYE BANQUE), not just accepted
  // Also require that there is at least one affaire
  const isFullyValidated = (primaryAffaire?.payment_status === 'PAYE BANQUE' || 
    (payment.affaires && payment.affaires.length > 0 && payment.affaires.every(a => a.payment_status === 'PAYE BANQUE')));

  const isPartiallyValidated = payment.affaires?.some(a => a.payment_status === 'PAYE BANQUE') || false;

  // Check allocation status
  const getAllocationStatus = () => {
    if (!payment.total_allocated || payment.total_allocated === 0) return 'unallocated';
    if (payment.is_fully_allocated) return 'fully';
    return 'partial';
  };

  const allocationStatus = getAllocationStatus();

  // Click outside handler for expanded affaires
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (expandedAffaires && !target.closest('.affaires-dropdown') && onToggleExpanded) {
        onToggleExpanded();
      }
    };

    if (expandedAffaires) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [expandedAffaires, onToggleExpanded]);

  const handleFMClick = (affaireId: string) => {
    const fmUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${affaireId}`;
    window.location.href = fmUrl;
  };

  const handleDetailClick = (affaireId: string) => {
    navigate(`${basePath}/expertise/${affaireId}`, {
      state: { from: `${basePath}/payment` }
    });
  };

  const handleSearchClick = () => {
    let cleanName = payment.name;
    cleanName = cleanName.replace(/^(M\.|Mme\.|Mlle\.|Mr\.|Mrs\.|Ms\.|Dr\.|MM\.|MME\.|MLLE\.)\s+/gi, '');
    cleanName = cleanName.replace(/\s+-\s+/g, ' ');
    cleanName = cleanName.replace(/\s+/g, ' ').trim();

    navigate(`${basePath}/search`, {
      state: {
        searchName: cleanName,
        tab: 'customer'
      }
    });
  };

  const handleTrackerClick = (affaireId: string) => {
    const scrollPosition = window.scrollY;
    navigate(`${basePath}/tracker/${affaireId}`, {
      state: { from: `${basePath}/payment`, scrollPosition }
    });
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleToggleCommunicationModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCommunicationModal(!showCommunicationModal);
  };

  const handleCloseCommunicationModal = () => {
    setShowCommunicationModal(false);
  };

  // Parse communication field into structured parts
  const parseCommunication = (fullCommunication: string) => {
    // The backend concatenates: communication_structured + communication_unstructured + remittance_info

    // Extract structured communication (between *** markers or first numeric sequence)
    const structuredMatch = fullCommunication.match(/\*{3}([^*]+)\*{3}/);
    let structuredComm = structuredMatch ? structuredMatch[1].trim() : null;

    // If no *** markers, look for numeric structured communication (e.g., 035006691223, 007006787724)
    if (!structuredComm) {
      const numericMatch = fullCommunication.match(/\b(\d{12})\b/);
      if (numericMatch) {
        structuredComm = numericMatch[1];
      }
    }

    // Extract client info from remittance_info section
    const lines = fullCommunication.split(/\r?\n/);
    const clientLines: string[] = [];
    let unstructuredComm: string | null = null;
    let inIBANSection = false;

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) continue;

      // Check if we hit the IBAN section
      if (trimmedLine.startsWith('IBAN:')) {
        inIBANSection = true;
        continue;
      }

      // If past IBAN section, look for custom communication messages
      if (inIBANSection) {
        // Skip generic labels
        if (trimmedLine === 'Communication :' || trimmedLine === 'Communication:') {
          continue;
        }

        // Extract custom communication (not just numbers or generic text)
        if (!trimmedLine.match(/^\d{12}$/) && // Not structured numeric code
            !trimmedLine.startsWith('Info personnelle:') && // Not info personnelle
            !trimmedLine.includes('***')) { // Not structured markers

          // This could be custom communication like "Remb. trop verse ONSS / MailDossier 810548"
          if (trimmedLine.length > 3 && !unstructuredComm) {
            unstructuredComm = trimmedLine;
          }
        }
        continue;
      }

      // Skip generic metadata lines
      if (trimmedLine.toLowerCase().includes('virement en euros') ||
          trimmedLine.toLowerCase().includes('virement instantané') ||
          trimmedLine.toLowerCase().startsWith('instantané le') ||
          trimmedLine === 'Belgique' ||
          trimmedLine.startsWith('Communication:') ||
          trimmedLine.startsWith('Communication :') ||
          trimmedLine.startsWith('Info personnelle:')) {
        continue;
      }

      // Skip lines with *** markers
      if (trimmedLine.includes('***')) {
        continue;
      }

      // Everything else is client info (name, address)
      clientLines.push(trimmedLine);
    }

    const clientInfo = clientLines.join('\n').trim();

    return {
      structuredCommunication: structuredComm,
      unstructuredCommunication: unstructuredComm,
      clientInfo: clientInfo || null,
      rawCommunication: fullCommunication
    };
  };

  const communicationParts = parseCommunication(payment.communication);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatAmount = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatIBAN = (iban: string) => {
    // Remove any existing spaces and format with space every 4 characters
    return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
  };

  const getStatusLabel = (status?: string) => {
    const trimmedStatus = status?.trim();
    switch (trimmedStatus) {
      case 'PAYE BANQUE':
      case 'validated':
        return 'Payé';
      case 'PREUVE VIREMENT':
        return 'Preuve';
      case 'facturé':
      case 'facturé au courtier':
        return 'Facturé';
      case 'facture à envoyer au courtier':
      case 'facture envoyée par mail au client':
        return 'Facture envoyée';
      case 'NOTE DE CREDIT REDIGEE':
        return 'Note de crédit';
      case 'pending':
        return 'Attente';
      default:
        return trimmedStatus || 'Inconnu';
    }
  };

  const getStatusColor = (status?: string) => {
    const trimmedStatus = status?.trim();
    if (trimmedStatus === 'PAYE BANQUE' || trimmedStatus === 'validated') {
      return 'bg-emerald-100 text-emerald-700';
    } else if (trimmedStatus === 'PREUVE VIREMENT') {
      return 'bg-purple-100 text-purple-700';
    } else if (trimmedStatus === 'facturé' || 
               trimmedStatus === 'facturé au courtier' ||
               trimmedStatus === 'facture à envoyer au courtier' ||
               trimmedStatus === 'facture envoyée par mail au client') {
      return 'bg-blue-100 text-blue-700';
    } else if (trimmedStatus === 'NOTE DE CREDIT REDIGEE') {
      return 'bg-indigo-100 text-indigo-700';
    } else if (trimmedStatus === 'pending') {
      return 'bg-amber-100 text-amber-700';
    } else {
      return 'bg-red-100 text-red-700';
    }
  };

  const getButtonGradient = (status?: string) => {
    const trimmedStatus = status?.trim();
    if (trimmedStatus === 'PAYE BANQUE' || trimmedStatus === 'validated') {
      return 'from-emerald-50 to-emerald-100 text-emerald-700 hover:from-emerald-100 hover:to-emerald-200';
    } else if (trimmedStatus === 'PREUVE VIREMENT') {
      return 'from-purple-50 to-purple-100 text-purple-700 hover:from-purple-100 hover:to-purple-200';
    } else if (trimmedStatus === 'facturé' || 
               trimmedStatus === 'facturé au courtier' ||
               trimmedStatus === 'facture à envoyer au courtier' ||
               trimmedStatus === 'facture envoyée par mail au client') {
      return 'from-blue-50 to-blue-100 text-blue-700 hover:from-blue-100 hover:to-blue-200';
    } else if (trimmedStatus === 'NOTE DE CREDIT REDIGEE') {
      return 'from-indigo-50 to-indigo-100 text-indigo-700 hover:from-indigo-100 hover:to-indigo-200';
    } else if (trimmedStatus === 'pending') {
      return 'from-amber-50 to-amber-100 text-amber-700 hover:from-amber-100 hover:to-amber-200';
    } else {
      return 'from-gray-50 to-gray-100 text-gray-700 hover:from-gray-100 hover:to-gray-200';
    }
  };

  const getAllocationBadge = () => {
    // Check if any affaire has an accepted discrepancy
    const hasAcceptedAffaire = payment.affaires?.some(a =>
      a.is_accepted && a.expected_amount && Math.abs(a.amount_allocated - a.expected_amount) > 0.01
    ) || (primaryAffaire?.is_accepted && primaryAffaire?.expected_amount &&
      Math.abs(primaryAffaire.amount_allocated - primaryAffaire.expected_amount) > 0.01);

    // If we have affaires allocated
    if (payment.affaires && payment.affaires.length > 0 && payment.total_expected) {
      // Check if primary affaire has multiple payments - use overall-based calculation
      const primaryAffaireId = primaryAffaire?.affaire_id || payment.affaires[0]?.affaire_id;
      const stats = primaryAffaireId && affairePaymentStats ? affairePaymentStats[primaryAffaireId] : null;

      // For multi-payment affaires, calculate Manque based on overall total
      if (stats && stats.paymentCount > 1 && stats.expectedAmount) {
        const overallManque = stats.expectedAmount - stats.totalAmount;

        if (Math.abs(overallManque) < 0.01) {
          // Overall payments match expected
          return (
            <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700">
              Validé (total)
            </span>
          );
        } else if (hasAcceptedAffaire) {
          return (
            <button
              onClick={() => onAcceptPayment && onAcceptPayment(payment, primaryAffaire)}
              className="px-2 py-0.5 text-xs font-medium rounded-md transition-colors cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200"
              title="Voir les détails de l'acceptation"
            >
              Différence acceptée
            </button>
          );
        } else if (overallManque > 0) {
          // Overall underpayment
          return (
            <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-red-100 text-red-700">
              Manque global {formatAmount(overallManque)}
            </span>
          );
        } else {
          // Overall overpayment
          return (
            <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700">
              Surplus global {formatAmount(Math.abs(overallManque))}
            </span>
          );
        }
      }

      // Single payment or no stats - use per-payment logic
      const discrepancy = payment.amount - payment.total_expected;

      if (Math.abs(discrepancy) < 0.01) {
        // Payment exactly matches expected amount
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700">
            Validé
          </span>
        );
      } else if (hasAcceptedAffaire) {
        // Discrepancy has been accepted - make it clickable with color based on payment status
        return (
          <button
            onClick={() => onAcceptPayment && onAcceptPayment(payment, primaryAffaire)}
            className="px-2 py-0.5 text-xs font-medium rounded-md transition-colors cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200"
            title="Voir les détails de l'acceptation"
          >
            Différence acceptée
          </button>
        );
      } else if (discrepancy < 0) {
        // Underpayment - client paid less than expected
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-red-100 text-red-700">
            Manque {formatAmount(Math.abs(discrepancy))}
          </span>
        );
      } else {
        // Overpayment - client paid more than expected
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700">
            Surplus {formatAmount(discrepancy)}
          </span>
        );
      }
    } else if (payment.affaires && payment.affaires.length > 0) {
      // Fallback: If we don't have total_expected, use the allocated amounts
      const unallocatedAmount = payment.unallocated_amount || (payment.amount - (payment.total_allocated || 0));

      if (Math.abs(unallocatedAmount) < 0.01) {
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700">
            Validé
          </span>
        );
      } else if (unallocatedAmount > 0) {
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-amber-100 text-amber-700">
            {formatAmount(unallocatedAmount)} non alloué
          </span>
        );
      }
    }

    // Original logic for payments without affaires
    if (allocationStatus === 'partial') {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-amber-100 text-amber-700">
          {formatAmount(payment.unallocated_amount || 0)} restant
        </span>
      );
    }

    return null;
  };

  // Helper function to render multi-payment badge for an affaire
  const renderMultiPaymentBadge = (affaireId: string) => {
    if (!affairePaymentStats || !onFilterByAffaire) return null;

    const stats = affairePaymentStats[affaireId];
    if (!stats || stats.paymentCount <= 1) return null;

    // Determine badge color based on overall manque/surplus
    const hasManque = stats.expectedAmount && stats.totalAmount < stats.expectedAmount - 0.01;
    const hasSurplus = stats.expectedAmount && stats.totalAmount > stats.expectedAmount + 0.01;
    const overallDiff = stats.expectedAmount ? Math.abs(stats.expectedAmount - stats.totalAmount) : 0;

    let badgeColors = 'bg-green-100 text-green-700 hover:bg-green-200';
    let totalColors = 'text-green-600';
    if (hasManque) {
      badgeColors = 'bg-red-100 text-red-700 hover:bg-red-200';
      totalColors = 'text-red-600';
    } else if (hasSurplus) {
      badgeColors = 'bg-blue-100 text-blue-700 hover:bg-blue-200';
      totalColors = 'text-blue-600';
    }

    const tooltipText = hasManque
      ? `${stats.paymentCount} paiements - Total: ${formatAmount(stats.totalAmount)} - Manque: ${formatAmount(overallDiff)}`
      : hasSurplus
      ? `${stats.paymentCount} paiements - Total: ${formatAmount(stats.totalAmount)} - Surplus: ${formatAmount(overallDiff)}`
      : `${stats.paymentCount} paiements - Total: ${formatAmount(stats.totalAmount)} (complet)`;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onFilterByAffaire(affaireId);
        }}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md transition-colors ml-1 ${badgeColors}`}
        title={tooltipText}
      >
        <DocumentDuplicateIcon className="h-3 w-3" />
        <span>{stats.paymentCount}</span>
        <span className={totalColors}>({formatAmount(stats.totalAmount)})</span>
      </button>
    );
  };

  return (
    <>
      <tr className={`transition-colors ${
        payment.is_ignored 
          ? 'bg-gray-100/50 opacity-60 hover:bg-gray-100/70' 
          : isFullyValidated 
          ? 'bg-emerald-50/30 hover:bg-emerald-50/50' 
          : 'hover:bg-gray-50/50'
      }`}>
        <td className="px-4 py-3 text-sm text-gray-900">
          {formatDate(payment.date)}
        </td>
        
        <td className="px-4 py-3 text-sm text-gray-900">
          <div 
            className="font-medium truncate max-w-[160px] cursor-pointer hover:text-blue-600 transition-colors relative group"
            title="Cliquez pour copier"
            onClick={() => handleCopy(payment.name, 'name')}
          >
            {payment.name}
            {copiedField === 'name' && (
              <span className="absolute -top-8 left-0 bg-emerald-600 text-white text-xs px-2 py-1 rounded-md shadow-lg animate-[fadeInScale_0.3s_ease-out] z-10">
                <span className="flex items-center gap-1">
                  <CheckIcon className="w-3 h-3" />
                  Copié!
                </span>
              </span>
            )}
          </div>
        </td>
        
        <td className="px-4 py-3 text-sm text-gray-600">
          <div className="relative">
            <div
              className="cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-2"
              onClick={handleToggleCommunicationModal}
            >
              <span className="block truncate max-w-[180px]">
                {payment.communication}
              </span>
              <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            </div>

            {showCommunicationModal && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] animate-[fadeIn_0.2s_ease-out]"
                  onClick={handleCloseCommunicationModal}
                />

                {/* Modal */}
                <div
                  className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1000] bg-white rounded-xl shadow-2xl w-full max-w-[500px] animate-[modalSlideIn_0.3s_ease-out]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Détails du paiement</h3>
                    <button
                      onClick={handleCloseCommunicationModal}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                      title="Fermer"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-4 space-y-4">
                    {/* IBAN Section */}
                    {payment.debtor_iban && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-gray-700">IBAN</label>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(formatIBAN(payment.debtor_iban!), 'iban');
                            }}
                            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                            title="Copier l'IBAN"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            {copiedField === 'iban' ? 'Copié!' : 'Copier'}
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                          <p className="text-sm font-mono text-gray-900 break-all">
                            {formatIBAN(payment.debtor_iban)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Client Section */}
                    {communicationParts.clientInfo && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-gray-700">Client</label>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(communicationParts.clientInfo!, 'client');
                            }}
                            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                            title="Copier les informations du client"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            {copiedField === 'client' ? 'Copié!' : 'Copier'}
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                            {communicationParts.clientInfo}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Communication Section */}
                    {(communicationParts.structuredCommunication || communicationParts.unstructuredCommunication) && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-semibold text-gray-700">Communication</label>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const commToCopy = communicationParts.structuredCommunication || communicationParts.unstructuredCommunication || '';
                              handleCopy(commToCopy, 'communication');
                            }}
                            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                            title="Copier la communication"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            {copiedField === 'communication' ? 'Copié!' : 'Copier'}
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                          <p className={`text-sm text-gray-900 break-all ${communicationParts.structuredCommunication ? 'font-mono' : ''}`}>
                            {communicationParts.structuredCommunication || communicationParts.unstructuredCommunication}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={handleCloseCommunicationModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </td>
        
        <td className="px-4 py-3 text-sm">
          <div className="flex flex-col gap-1">
            <span className="font-medium text-gray-900">
              {formatAmount(payment.amount, payment.currency)}
            </span>
            {getAllocationBadge()}
          </div>
        </td>
        
        <td className="px-4 py-3 text-sm">
          {hasMultipleAffaires ? (
            <div className="relative affaires-dropdown">
              <button
                onClick={() => setExpandedAffaires(!expandedAffaires)}
                className="group flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 shadow-sm hover:shadow"
              >
                <div className="flex items-center gap-1.5">
                  <CurrencyEuroIcon className="h-3.5 w-3.5" />
                  <span className="font-semibold">{payment.affaires?.length}</span>
                  <span className="text-blue-600">affaires</span>
                </div>
                <svg 
                  className={`w-3 h-3 transition-transform duration-200 ${expandedAffaires ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {isPartiallyValidated && !isFullyValidated && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-medium rounded-md bg-amber-100 text-amber-700">
                    Partiel
                  </span>
                )}
              </button>
              
              {expandedAffaires && payment.affaires && (
                <div className="absolute z-10 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[260px] animate-[fadeIn_0.2s_ease-out]">
                  <div className="p-2 space-y-1">
                    {payment.affaires.map((affaire) => (
                      <div
                        key={affaire.affaire_id}
                        className={`py-1.5 px-2 rounded-md hover:bg-gray-50 transition-colors ${
                          affaire.is_accepted && affaire.expected_amount &&
                          Math.abs(affaire.amount_allocated - affaire.expected_amount) > 0.01
                            ? 'bg-amber-50/30'
                            : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-gray-900 text-xs">{affaire.affaire_id}</span>
                            {renderMultiPaymentBadge(affaire.affaire_id)}
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getStatusColor(affaire.payment_status)}`}>
                              {getStatusLabel(affaire.payment_status)}
                            </span>
                          </div>
                          <span className="font-semibold text-gray-900 text-xs">
                            {formatAmount(affaire.amount_allocated)}
                          </span>
                        </div>
                        {/* Invoice link badge */}
                        {affaire.invoice_number && (
                          <div className="flex items-center gap-1 text-[10px] mt-0.5">
                            <DocumentDuplicateIcon className="w-3 h-3 text-indigo-500" />
                            <span className="text-indigo-700 font-medium">{affaire.invoice_number}</span>
                            <span className={`px-1 py-0 rounded text-[9px] font-medium ${
                              affaire.invoice_status === 'PAID' ? 'bg-green-100 text-green-700' :
                              affaire.invoice_status === 'SENT' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {affaire.invoice_status === 'PAID' ? 'Payé' :
                               affaire.invoice_status === 'SENT' ? 'Envoyé' :
                               affaire.invoice_status || ''}
                            </span>
                          </div>
                        )}
                        {/* Compact acceptance indicator */}
                        {affaire.is_accepted && affaire.expected_amount &&
                         Math.abs(affaire.amount_allocated - affaire.expected_amount) > 0.01 && (
                          <div className="flex items-center gap-1 text-[10px] text-amber-700 mt-1">
                            <CheckCircleIcon className="w-3 h-3" />
                            <span>Accepté</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Footer with totals */}
                  <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 rounded-b-lg space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Total alloué:</span>
                      <span className="font-semibold text-gray-900">{formatAmount(payment.total_allocated || 0)}</span>
                    </div>
                    {payment.total_expected && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Attendu:</span>
                        <span className="font-semibold text-gray-900">{formatAmount(payment.total_expected)}</span>
                      </div>
                    )}
                    {payment.total_expected && Math.abs((payment.total_allocated || 0) - payment.total_expected) > 0.01 && (
                      <div className={`flex justify-between text-xs font-bold pt-1 border-t border-gray-200 ${
                        (payment.total_allocated || 0) > payment.total_expected ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        <span>{(payment.total_allocated || 0) > payment.total_expected ? 'Surplus:' : 'Manque:'}</span>
                        <span>{formatAmount(Math.abs((payment.total_allocated || 0) - payment.total_expected))}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : primaryAffaire ? (
            <div className="relative affaires-dropdown">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setExpandedAffaires(!expandedAffaires)}
                  className={`group flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r ${getButtonGradient(primaryAffaire.payment_status)} rounded-lg transition-all duration-200 shadow-sm hover:shadow`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">{primaryAffaire.affaire_id}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${getStatusColor(primaryAffaire.payment_status)}`}>
                      {getStatusLabel(primaryAffaire.payment_status)}
                    </span>
                  </div>
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ml-1 ${expandedAffaires ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {renderMultiPaymentBadge(primaryAffaire.affaire_id)}
              </div>
              
              {expandedAffaires && (
                <div className="absolute z-10 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[260px] animate-[fadeIn_0.2s_ease-out]">
                  {(() => {
                    const stats = primaryAffaire && affairePaymentStats ? affairePaymentStats[primaryAffaire.affaire_id] : null;
                    const isMultiPayment = stats && stats.paymentCount > 1;
                    const overallManque = stats?.expectedAmount ? stats.expectedAmount - stats.totalAmount : 0;

                    return (
                      <>
                        {/* Header with affaire ID and status */}
                        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                          <span className="text-sm font-semibold text-gray-700">Affaire {primaryAffaire.affaire_id}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${getStatusColor(primaryAffaire.payment_status)}`}>
                            {getStatusLabel(primaryAffaire.payment_status)}
                          </span>
                        </div>

                        {/* Multi-payment: Compact payment history */}
                        {isMultiPayment && stats.payments ? (
                          <div className="p-2">
                            <div className="space-y-1 max-h-28 overflow-y-auto">
                              {stats.payments.map((p) => (
                                <div
                                  key={p.id}
                                  className={`flex justify-between text-xs px-2 py-1.5 rounded ${
                                    p.id === payment.id ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                                  }`}
                                >
                                  <span className="text-gray-600">
                                    {new Date(p.date).toLocaleDateString('fr-BE')}
                                    {p.id === payment.id && <span className="text-blue-600 ml-1">•</span>}
                                  </span>
                                  <span className="font-medium">{formatAmount(p.amount)}</span>
                                </div>
                              ))}
                            </div>
                            {/* Totals */}
                            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">Total reçu:</span>
                                <span className="font-semibold">{formatAmount(stats.totalAmount)}</span>
                              </div>
                              {stats.expectedAmount && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-600">Attendu:</span>
                                  <span className="font-semibold">{formatAmount(stats.expectedAmount)}</span>
                                </div>
                              )}
                              {Math.abs(overallManque) > 0.01 && (
                                <div className={`flex justify-between text-xs font-bold pt-1 border-t border-gray-100 ${
                                  overallManque > 0 ? 'text-red-600' : 'text-blue-600'
                                }`}>
                                  <span>{overallManque > 0 ? 'Manque:' : 'Surplus:'}</span>
                                  <span>{formatAmount(Math.abs(overallManque))}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          /* Single payment: Show allocation details */
                          <div className="p-2">
                            <div className="space-y-1 bg-gray-50 rounded-md p-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">Alloué:</span>
                                <span className="font-semibold text-gray-900">{formatAmount(primaryAffaire.amount_allocated)}</span>
                              </div>
                              {primaryAffaire.expected_amount && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-600">Attendu:</span>
                                  <span className="font-semibold text-gray-900">{formatAmount(primaryAffaire.expected_amount)}</span>
                                </div>
                              )}
                              {primaryAffaire.expected_amount && Math.abs(primaryAffaire.amount_allocated - primaryAffaire.expected_amount) > 0.01 && (
                                <div className={`flex justify-between text-xs pt-1 border-t border-gray-200 font-bold ${
                                  primaryAffaire.amount_allocated > primaryAffaire.expected_amount ? 'text-blue-600' : 'text-red-600'
                                }`}>
                                  <span>{primaryAffaire.amount_allocated > primaryAffaire.expected_amount ? 'Surplus:' : 'Manque:'}</span>
                                  <span>{formatAmount(Math.abs(primaryAffaire.amount_allocated - primaryAffaire.expected_amount))}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Acceptance Info (if applicable) */}
                        {primaryAffaire.is_accepted && primaryAffaire.expected_amount &&
                         Math.abs(primaryAffaire.amount_allocated - primaryAffaire.expected_amount) > 0.01 && (
                          <div className="border-t border-amber-200 bg-amber-50/50 p-2">
                            <div className="flex items-center gap-1.5 text-xs text-amber-700">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              <span className="font-medium">Accepté</span>
                              {primaryAffaire.acceptance_date && (
                                <span className="text-amber-600 ml-auto">
                                  {new Date(primaryAffaire.acceptance_date).toLocaleDateString('fr-BE')}
                                </span>
                              )}
                            </div>
                            {primaryAffaire.acceptance_note && (
                              <div className="mt-1 text-xs text-gray-600 italic truncate" title={primaryAffaire.acceptance_note}>
                                "{primaryAffaire.acceptance_note}"
                              </div>
                            )}
                          </div>
                        )}

                        {/* Validation Info */}
                        {primaryAffaire.is_validated && (
                          <div className="border-t border-gray-100 px-3 py-1.5 bg-gray-50 rounded-b-lg">
                            <div className="flex items-center gap-1.5 text-xs text-green-600">
                              <CheckIcon className="w-3 h-3" />
                              <span>Validé</span>
                              {primaryAffaire.validation_date && (
                                <span className="text-gray-500 ml-auto">
                                  {new Date(primaryAffaire.validation_date).toLocaleDateString('fr-BE')}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          )}
        </td>
        
        <td className="px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            {hasAnyAffaire ? (
              <>
                {primaryAffaire && (
                  <>
                    <button
                      onClick={() => handleFMClick(primaryAffaire.affaire_id)}
                      title="Ouvrir dans FileMaker"
                      className="inline-flex items-center px-2.5 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1" />
                      FM
                    </button>
                    <button
                      onClick={() => handleDetailClick(primaryAffaire.affaire_id)}
                      title="Voir les détails"
                      className="px-2.5 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Détails
                    </button>
                    <button
                      onClick={() => handleTrackerClick(primaryAffaire.affaire_id)}
                      title="Voir le tracker"
                      className="inline-flex items-center px-2.5 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-200 transition-colors"
                    >
                      <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 mr-1" />
                      Tracker
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => onSmartAllocate(payment)}
                  title="Modifier l'allocation"
                  className="inline-flex items-center justify-center p-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </button>
                
                {/* Accept button for over/under payments - uses overall stats for multi-payment affaires */}
                {primaryAffaire && primaryAffaire.expected_amount &&
                 !primaryAffaire.is_accepted &&
                 !primaryAffaire.is_validated &&
                 onAcceptPayment && (() => {
                   // Check if this is a multi-payment affaire with overall stats
                   const stats = affairePaymentStats?.[primaryAffaire.affaire_id];
                   if (stats && stats.paymentCount > 1 && stats.expectedAmount) {
                     // Multi-payment: use overall difference - hide button if overall is complete
                     const overallDiff = Math.abs(stats.expectedAmount - stats.totalAmount);
                     return overallDiff > 0.01; // Show button only if overall has difference
                   }
                   // Single payment: use per-payment difference
                   return Math.abs(primaryAffaire.amount_allocated - primaryAffaire.expected_amount) > 0.01;
                 })() && (
                  <button
                    onClick={() => onAcceptPayment(payment, primaryAffaire)}
                    title="Accepter le paiement avec une note"
                    className="inline-flex items-center px-2.5 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                    Accepter
                  </button>
                )}
                
              </>
            ) : (
              <>
                <button
                  onClick={handleSearchClick}
                  title="Rechercher le client"
                  className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <MagnifyingGlassIcon className="h-3.5 w-3.5 mr-1" />
                  Recherche
                </button>
                <button
                  onClick={() => onSmartAllocate(payment)}
                  title="Répartition intelligente"
                  className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-lg hover:bg-purple-200 transition-colors"
                >
                  <SparklesIcon className="h-3.5 w-3.5 mr-1" />
                  Allocation Smart
                </button>
              </>
            )}
            
            {/* Ignore button - show for all payments */}
            {onIgnore && (
              <button
                onClick={() => onIgnore(payment.id, !payment.is_ignored)}
                title={payment.is_ignored ? "Restaurer ce paiement" : "Ignorer ce paiement (pas de notre business)"}
                className={`inline-flex items-center justify-center p-1.5 text-xs font-medium rounded-lg transition-colors ${
                  payment.is_ignored 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                {payment.is_ignored ? (
                  <EyeIcon className="h-3.5 w-3.5" />
                ) : (
                  <EyeSlashIcon className="h-3.5 w-3.5" />
                )}
              </button>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};